import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const CartCtx = createContext(null);
const KEY = 'dg_cart_v1';

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; }
    });

    useEffect(() => {
        localStorage.setItem(KEY, JSON.stringify(items));
    }, [items]);

    const addItem = (product, qty = 1, resolvedPrice, unitCode) => {
        setItems(cur => {
            const found = cur.find(x => x.product_id === product.id && x.unit_code === unitCode);
            if (found) {
                return cur.map(x => (x.product_id === product.id && x.unit_code === unitCode) ? { ...x, qty: x.qty + qty } : x);
            }
            return [...cur, {
                product_id: product.id, sku: product.sku, name: product.name,
                image: (product.images || [])[0] || null,
                unit_price: resolvedPrice,
                unit_code: unitCode || product.retail_unit_code || 'pcs',
                weight_kg: (product.logistics || {}).weight_kg || 0.5,
                qty,
            }];
        });
    };

    const updateQty = (product_id, qty) => setItems(cur => cur.map(x => x.product_id === product_id ? { ...x, qty: Math.max(1, qty) } : x));
    const removeItem = (product_id) => setItems(cur => cur.filter(x => x.product_id !== product_id));
    const clear = () => setItems([]);

    const subtotal = useMemo(() => items.reduce((s, x) => s + x.unit_price * x.qty, 0), [items]);
    const totalWeight = useMemo(() => items.reduce((s, x) => s + x.weight_kg * x.qty, 0), [items]);
    const totalQty = useMemo(() => items.reduce((s, x) => s + x.qty, 0), [items]);

    // When customer type / level changes, refresh unit prices
    const rePrice = (getNewPrice) => {
        setItems(cur => cur.map(x => {
            const np = getNewPrice(x.product_id);
            return np != null ? { ...x, unit_price: np } : x;
        }));
    };

    return (
        <CartCtx.Provider value={{ items, addItem, updateQty, removeItem, clear, subtotal, totalWeight, totalQty, rePrice }}>
            {children}
        </CartCtx.Provider>
    );
}

export function useCart() { return useContext(CartCtx); }
